/**
 * Enhanced Document Viewer
 * Provides inline viewing for PDFs, Word documents, and other file types
 * with a modal interface similar to the image lightbox
 */

//  Enhanced openDoc with inline document viewing 
// Allow the app to configure the API URL once at startup
let _configuredApiUrl = '';
export function setApiUrl(url) { _configuredApiUrl = url; }

function openDocEnhanced(rawUrl) {
  if (!rawUrl) return;

  // Use configured URL, then env var (replaced at build time by Webpack), then empty
  const API_URL = _configuredApiUrl
    || (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL)
    || '';

  // Extract category from hash
  const hashCat = rawUrl.match(/#cat=(image|pdf|doc|sheet)/)?.[1] || null;

  // Clean URL
  const url = rawUrl.split('#')[0]
    .replace('/image/upload/fl_inline/', '/image/upload/')
    .replace('/raw/upload/fl_inline/', '/raw/upload/');

  const ext = url.split('?')[0].split('.').pop()?.toLowerCase() || '';
  const extCat = (() => {
    if (url.startsWith('gdrive://')) return 'pdf';
    if (['jpg','jpeg','png','gif','webp','heic','heif','bmp','tiff','tif','svg'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (['doc','docx','odt','txt'].includes(ext)) return 'doc';
    if (['xls','xlsx','ods','csv','ppt','pptx'].includes(ext)) return 'sheet';
    return 'pdf';
  })();
  const cat = hashCat || extCat;

  // Images: in-app lightbox
  if (cat === 'image') {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.92);display:flex;flex-direction:column;';
    const bar = document.createElement('div');
    bar.style.cssText = 'background:#294c79;color:#fff;padding:10px 20px;display:flex;justify-content:space-between;align-items:center;flex-shrink:0;gap:12px;';
    bar.innerHTML = '<span style="font-weight:700;font-size:14px;">Image Viewer</span>';
    // No raw URL exposed in header
    const closeBtn = document.createElement('button');
    closeBtn.textContent = 'Close';
    closeBtn.style.cssText = 'background:rgba(255,255,255,0.2);border:none;color:#fff;padding:7px 16px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:600;';
    const remove = () => { try { document.body.removeChild(overlay); } catch(e){} document.removeEventListener('keydown', esc); };
    closeBtn.onclick = remove;
    bar.appendChild(closeBtn);
    overlay.appendChild(bar);
    const esc = e => { if (e.key === 'Escape') remove(); };
    document.addEventListener('keydown', esc);
    overlay.addEventListener('click', e => { if (e.target === overlay) remove(); });
    const body = document.createElement('div');
    body.style.cssText = 'flex:1;display:flex;align-items:center;justify-content:center;padding:20px;overflow:auto;';
    const img = document.createElement('img');
    img.src = url;
    img.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain;border-radius:4px;box-shadow:0 4px 32px rgba(0,0,0,0.6);';
    img.onerror = () => { 
      img.style.display = 'none';
      const errMsg = document.createElement('p');
      errMsg.textContent = 'Could not load image. Please try again.';
      errMsg.style.cssText = 'color:#fff;text-align:center;padding:20px;';
      body.appendChild(errMsg);
    };
    body.appendChild(img);
    overlay.appendChild(body);
    document.body.appendChild(overlay);
    return;
  }

  // PDFs and docs: inline viewer with modal
  // Create modal overlay
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;padding:20px;';
  
  const modal = document.createElement('div');
  modal.style.cssText = 'background:white;border-radius:12px;width:100%;max-width:90vw;height:90vh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,0.3);';

  // Header
  const header = document.createElement('div');
  header.style.cssText = 'background:#294c79;color:#fff;padding:16px 24px;display:flex;justify-content:space-between;align-items:center;flex-shrink:0;border-radius:12px 12px 0 0;';
  
  const title = document.createElement('div');
  title.style.cssText = 'display:flex;align-items:center;gap:12px;';
  const icon = document.createElement('span');
  icon.textContent = cat === 'pdf' ? 'PDF' : cat === 'doc' ? 'DOC' : 'SHEET';
  icon.style.cssText = 'font-size:24px;';
  const titleText = document.createElement('span');
  titleText.textContent = cat === 'pdf' ? 'PDF Document' : cat === 'doc' ? 'Word Document' : 'Spreadsheet';
  titleText.style.cssText = 'font-weight:700;font-size:16px;';
  title.appendChild(icon);
  title.appendChild(titleText);

  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'Close';
  closeBtn.style.cssText = 'background:rgba(255,255,255,0.2);border:none;color:#fff;padding:8px 16px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:600;hover:background:rgba(255,255,255,0.3);';
  
  header.appendChild(title);
  header.appendChild(closeBtn);
  modal.appendChild(header);

  // Content area
  const content = document.createElement('div');
  content.style.cssText = 'flex:1;overflow:auto;background:#f5f5f5;padding:20px;display:flex;align-items:center;justify-content:center;';
  
  // Loading spinner
  const spinner = document.createElement('div');
  spinner.style.cssText = 'text-align:center;';
  const spinnerAnim = document.createElement('div');
  spinnerAnim.style.cssText = 'width:48px;height:48px;border:4px solid #294c79;border-top-color:transparent;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 16px;';
  const spinnerText = document.createElement('p');
  spinnerText.textContent = 'Loading document...';
  spinnerText.style.cssText = 'color:#666;font-weight:500;margin:0;';
  spinner.appendChild(spinnerAnim);
  spinner.appendChild(spinnerText);
  content.appendChild(spinner);

  // Add CSS animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);

  modal.appendChild(content);
  overlay.appendChild(modal);

  //Close handler
  const remove = () => {
    try { document.body.removeChild(overlay); } catch(e){}
    document.removeEventListener('keydown', esc);
  };

  closeBtn.onclick = remove;
  overlay.onclick = (e) => { if (e.target === overlay) remove(); };
  const esc = (e) => { if (e.key === 'Escape') remove(); };
  document.addEventListener('keydown', esc);

  document.body.appendChild(overlay);

  //  Fetch document via secure token 
  const authToken = localStorage.getItem('authToken') ||
                    localStorage.getItem('adminAuthToken') ||
                    localStorage.getItem('token') || '';

  fetch(`${API_URL}/api/request-view-token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    },
    body: JSON.stringify({ url }),
  })
    .then(r => r.json())
    .then(data => {
      if (!data.token) throw new Error(data.error || 'No token returned');
      
      const viewUrl = `${API_URL}/api/view-document?token=${encodeURIComponent(data.token)}`;

      // Load document based on type
      if (cat === 'pdf') {
        loadPDFInline(viewUrl, content, url);
      } else if (cat === 'doc') {
        loadDocxInline(viewUrl, content);
      } else if (cat === 'sheet') {
        loadSheetInline(viewUrl, content);
      } else {
        loadGenericDocument(viewUrl, content, ext);
      }
    })
    .catch(err => {
      console.error('openDocEnhanced error:', err);
      content.innerHTML = `
        <div style="background:#fee;border:1px solid #fcc;border-radius:8px;padding:24px;text-align:center;max-width:400px;">
          <p style="color:#c33;font-weight:700;margin:0 0 12px;"> Error Loading Document</p>
          <p style="color:#666;margin:0 0 16px;font-size:14px;">${err.message}</p>
          <p style="color:#888;font-size:12px;">Please close this dialog and try again. If the problem persists, contact support.</p>
        </div>
      `;
    });
}

/* global pdfjsLib, mammoth */
//  Load PDF inline using PDF.js 
function loadPDFInline(pdfUrl, container, originalUrl) {
  if (typeof pdfjsLib === 'undefined') {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      renderPDFDocument(pdfUrl, container, originalUrl);
    };
    script.onerror = () => renderPDFIframe(pdfUrl, container, originalUrl);
    document.head.appendChild(script);
  } else {
    if (pdfjsLib.GlobalWorkerOptions && !pdfjsLib.GlobalWorkerOptions.workerSrc) {
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    }
    renderPDFDocument(pdfUrl, container, originalUrl);
  }
}

function renderPDFIframe(pdfUrl, container, originalUrl) {
  container.innerHTML = '';
  container.style.cssText = 'display:flex;flex-direction:column;height:100%;background:#525659;';
  const bar = document.createElement('div');
  bar.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;padding:8px;background:#323639;';
  const downloadBtn = document.createElement('button');
  downloadBtn.textContent = 'Download';
  downloadBtn.style.cssText = 'background:#a5c250;color:#fff;border:none;padding:7px 14px;border-radius:6px;cursor:pointer;font-weight:600;font-size:13px;';
  downloadBtn.onclick = function() { triggerDownload(originalUrl, 'document.pdf'); };
  bar.appendChild(downloadBtn);
  container.appendChild(bar);
  const frameWrap = document.createElement('div');
  frameWrap.style.cssText = 'flex:1;min-height:0;background:#fff;';
  frameWrap.innerHTML = '<div style="padding:24px;text-align:center;color:#666;">Loading PDF preview...</div>';
  container.appendChild(frameWrap);

  fetch(pdfUrl)
    .then(function(r) {
      if (!r.ok) return r.text().then(function(t) { throw new Error(t || 'Could not load PDF'); });
      return r.blob();
    })
    .then(function(blob) {
      const objUrl = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.src = objUrl;
      iframe.title = 'PDF Document';
      iframe.style.cssText = 'border:0;width:100%;height:100%;background:#fff;';
      frameWrap.innerHTML = '';
      frameWrap.appendChild(iframe);
    })
    .catch(function(err) {
      frameWrap.innerHTML = `
        <div style="padding:24px;text-align:center;color:#666;">
          <p style="color:#c33;font-weight:700;margin:0 0 12px;">Could not preview PDF</p>
          <p style="font-size:13px;margin:0 0 12px;">${err.message}</p>
          <button style="background:#294c79;color:#fff;border:none;padding:8px 14px;border-radius:8px;cursor:pointer;">Open in new tab</button>
        </div>
      `;
      frameWrap.querySelector('button').onclick = function() { window.open(pdfUrl, '_blank', 'noopener,noreferrer'); };
    });
}

function renderPDFDocument(pdfUrl, container, originalUrl) {
  container.innerHTML = '';
  container.style.cssText = 'display:flex;flex-direction:column;height:100%;overflow:hidden;background:#525659;';

  //  Controls bar 
  const controls = document.createElement('div');
  controls.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:6px;padding:8px 12px;background:#323639;flex-shrink:0;flex-wrap:wrap;';

  const btnStyle = 'background:#294c79;color:#fff;border:none;padding:7px 14px;border-radius:6px;cursor:pointer;font-weight:600;font-size:13px;touch-action:manipulation;';

  const prevBtn = document.createElement('button');
  prevBtn.textContent = 'Prev';
  prevBtn.style.cssText = btnStyle;

  const pageInput = document.createElement('input');
  pageInput.type = 'number';
  pageInput.min = '1';
  pageInput.value = '1';
  pageInput.style.cssText = 'width:48px;padding:6px 4px;border:1px solid #555;border-radius:6px;text-align:center;font-weight:700;font-size:14px;background:#fff;color:#333;';

  const pageInfo = document.createElement('span');
  pageInfo.style.cssText = 'color:#ccc;font-size:13px;font-weight:600;white-space:nowrap;';
  pageInfo.textContent = 'of ...';

  const nextBtn = document.createElement('button');
  nextBtn.textContent = 'Next';
  nextBtn.style.cssText = btnStyle;

  // Zoom controls
  const zoomOutBtn = document.createElement('button');
  zoomOutBtn.textContent = '-';
  zoomOutBtn.title = 'Zoom out';
  zoomOutBtn.style.cssText = btnStyle + 'padding:7px 12px;font-size:16px;';

  const zoomLabel = document.createElement('span');
  zoomLabel.style.cssText = 'color:#ccc;font-size:12px;font-weight:600;min-width:36px;text-align:center;';
  zoomLabel.textContent = '100%';

  const zoomInBtn = document.createElement('button');
  zoomInBtn.textContent = '+';
  zoomInBtn.title = 'Zoom in';
  zoomInBtn.style.cssText = btnStyle + 'padding:7px 12px;font-size:16px;';

  // Download button  fetches fresh token then downloads
  const downloadBtn = document.createElement('button');
  downloadBtn.textContent = 'Download';
  downloadBtn.style.cssText = 'background:#a5c250;color:#fff;border:none;padding:7px 14px;border-radius:6px;cursor:pointer;font-weight:600;font-size:13px;touch-action:manipulation;';
  downloadBtn.onclick = function() {
    downloadBtn.textContent = '...';
    downloadBtn.disabled = true;
    triggerDownload(originalUrl, 'document.pdf').finally(function() {
      downloadBtn.textContent = 'Download';
      downloadBtn.disabled = false;
    });
  };

  controls.appendChild(prevBtn);
  controls.appendChild(pageInput);
  controls.appendChild(pageInfo);
  controls.appendChild(nextBtn);
  // Separator
  const sep = document.createElement('span');
  sep.style.cssText = 'width:1px;height:24px;background:#555;margin:0 4px;';
  controls.appendChild(sep);
  controls.appendChild(zoomOutBtn);
  controls.appendChild(zoomLabel);
  controls.appendChild(zoomInBtn);
  controls.appendChild(downloadBtn);
  container.appendChild(controls);

  //  Canvas area  scrollable, supports pinch-to-zoom via CSS 
  const canvasWrapper = document.createElement('div');
  canvasWrapper.style.cssText = [
    'flex:1;overflow:auto;display:flex;align-items:flex-start;',
    'justify-content:center;padding:16px;box-sizing:border-box;',
    // Allow browser native pinch-to-zoom on mobile
    'touch-action:pan-x pan-y;-webkit-overflow-scrolling:touch;'
  ].join('');

  const canvasContainer = document.createElement('div');
  canvasContainer.style.cssText = 'display:inline-block;transform-origin:top center;transition:transform 0.1s;';

  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'display:block;box-shadow:0 4px 16px rgba(0,0,0,0.4);background:#fff;';
  canvasContainer.appendChild(canvas);
  canvasWrapper.appendChild(canvasContainer);
  container.appendChild(canvasWrapper);

  const loadingMsg = document.createElement('div');
  loadingMsg.style.cssText = 'color:#ccc;text-align:center;padding:40px;font-size:15px;position:absolute;';
  loadingMsg.textContent = 'Loading PDF...';
  canvasWrapper.appendChild(loadingMsg);

  pdfjsLib.getDocument({ url: pdfUrl, withCredentials: false }).promise
    .then(pdf => {
      try { canvasWrapper.removeChild(loadingMsg); } catch(e) {}
      let currentPage = 1;
      let userScale = 1.0; // zoom multiplier controlled by +/- buttons
      const totalPages = pdf.numPages;
      pageInput.max = String(totalPages);
      pageInfo.textContent = 'of ' + totalPages;

      function updateZoomLabel() {
        zoomLabel.textContent = Math.round(userScale * 100) + '%';
      }

      function renderCurrentPage() {
        pdf.getPage(currentPage).then(function(page) {
          // Base scale: fit to container width
          var containerWidth = canvasWrapper.clientWidth - 32;
          var baseViewport = page.getViewport({ scale: 1 });
          var baseScale = Math.max(0.6, containerWidth / baseViewport.width);
          var finalScale = baseScale * userScale;
          var viewport = page.getViewport({ scale: finalScale });

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport }).promise.then(function() {});
        });
      }

      function goToPage(n) {
        currentPage = Math.max(1, Math.min(totalPages, n));
        pageInput.value = String(currentPage);
        prevBtn.style.opacity = currentPage === 1 ? '0.4' : '1';
        nextBtn.style.opacity = currentPage === totalPages ? '0.4' : '1';
        renderCurrentPage();
        canvasWrapper.scrollTo(0, 0);
      }

      prevBtn.onclick = function() { goToPage(currentPage - 1); };
      nextBtn.onclick = function() { goToPage(currentPage + 1); };
      pageInput.onchange = function() { goToPage(parseInt(pageInput.value) || 1); };
      pageInput.onkeydown = function(e) { if (e.key === 'Enter') goToPage(parseInt(pageInput.value) || 1); };

      zoomInBtn.onclick = function() {
        userScale = Math.min(4, userScale + 0.25);
        updateZoomLabel();
        renderCurrentPage();
      };
      zoomOutBtn.onclick = function() {
        userScale = Math.max(0.25, userScale - 0.25);
        updateZoomLabel();
        renderCurrentPage();
      };

      // Pinch-to-zoom on mobile using touch events
      var lastDist = 0;
      canvasWrapper.addEventListener('touchstart', function(e) {
        if (e.touches.length === 2) {
          lastDist = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
        }
      }, { passive: true });
      canvasWrapper.addEventListener('touchmove', function(e) {
        if (e.touches.length === 2) {
          var dist = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
          if (lastDist > 0) {
            var ratio = dist / lastDist;
            userScale = Math.min(4, Math.max(0.25, userScale * ratio));
            updateZoomLabel();
            renderCurrentPage();
          }
          lastDist = dist;
        }
      }, { passive: true });
      canvasWrapper.addEventListener('touchend', function() { lastDist = 0; }, { passive: true });

      updateZoomLabel();
      goToPage(1);
    })
    .catch(function(err) {
      console.error('PDF.js render failed:', err);
      renderPDFIframe(pdfUrl, container, originalUrl);
    });
}

function renderPage() {} // kept for compatibility  not used

function sanitizeViewerHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(String(html || ''), 'text/html');
  doc.querySelectorAll('script, iframe, object, embed, link, meta, style').forEach(node => node.remove());
  doc.querySelectorAll('*').forEach(node => {
    Array.from(node.attributes).forEach(attr => {
      const name = attr.name.toLowerCase();
      const value = String(attr.value || '').trim().toLowerCase();
      if (name.startsWith('on') || value.startsWith('javascript:') || value.startsWith('data:text/html')) {
        node.removeAttribute(attr.name);
      }
    });
  });
  return doc.body.innerHTML;
}

//  Load DOCX inline using Mammoth.js 

function triggerDownload(originalUrl, filename) {
  // Use the dedicated /api/download-document endpoint with auth header
  // This works even after the view token has been consumed
  var authToken = localStorage.getItem('authToken') ||
                  localStorage.getItem('adminAuthToken') ||
                  localStorage.getItem('token') || '';
  return fetch(_configuredApiUrl + '/api/download-document', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + authToken,
    },
    body: JSON.stringify({ url: originalUrl }),
  })
    .then(function(r) {
      if (!r.ok) return r.text().then(function(t) { throw new Error(t || 'Download failed'); });
      return r.blob();
    })
    .then(function(blob) {
      var objUrl = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = objUrl;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      setTimeout(function() { URL.revokeObjectURL(objUrl); document.body.removeChild(a); }, 3000);
    })
    .catch(function(err) {
      alert('Download failed: ' + err.message);
      console.error('Download error:', err);
    });
}

function addDownloadBar(container, url, filename) {
  const bar = document.createElement('div');
  bar.style.cssText = 'display:flex;justify-content:flex-end;padding:8px 16px;background:#323639;flex-shrink:0;';
  const btn = document.createElement('button');
  btn.textContent = 'Download';
  btn.style.cssText = 'background:#a5c250;color:#fff;border:none;padding:7px 16px;border-radius:6px;font-weight:600;font-size:13px;cursor:pointer;';
  btn.onclick = function() {
    btn.textContent = '...';
    btn.disabled = true;
    triggerDownload(url, filename).finally(function() {
      btn.textContent = 'Download';
      btn.disabled = false;
    });
  };
  bar.appendChild(btn);
  container.parentElement && container.parentElement.insertBefore(bar, container);
}

function loadDocxInline(docxUrl, container) {
  // Load Mammoth.js dynamically
  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/mammoth@1.6.0/mammoth.browser.min.js';
  script.onload = () => {
    fetch(docxUrl)
      .then(response => response.arrayBuffer())
      .then(arrayBuffer => mammoth.convertToHtml({ arrayBuffer }))
      .then(result => {
        const docContainer = document.createElement('div');
        docContainer.style.cssText = 'background:#fff;border-radius:8px;padding:32px;max-width:900px;margin:0 auto;box-shadow:0 2px 8px rgba(0,0,0,0.1);overflow-y:auto;max-height:100%;';
        docContainer.innerHTML = sanitizeViewerHtml(result.value);
        
        // Style the document content
        const style = document.createElement('style');
        style.textContent = `
          .doc-container p { margin: 12px 0; line-height: 1.6; color: #333; }
          .doc-container h1 { font-size: 28px; font-weight: 700; margin: 24px 0 12px; color: #294c79; }
          .doc-container h2 { font-size: 22px; font-weight: 700; margin: 20px 0 10px; color: #294c79; }
          .doc-container h3 { font-size: 18px; font-weight: 600; margin: 16px 0 8px; color: #294c79; }
          .doc-container table { width: 100%; border-collapse: collapse; margin: 16px 0; }
          .doc-container td, .doc-container th { border: 1px solid #ddd; padding: 8px; text-align: left; }
          .doc-container th { background: #f0f0f0; font-weight: 600; }
          .doc-container ul, .doc-container ol { margin: 12px 0; padding-left: 24px; }
          .doc-container li { margin: 6px 0; }
          .doc-container strong { font-weight: 700; }
          .doc-container em { font-style: italic; }
        `;
        document.head.appendChild(style);
        
        docContainer.className = 'doc-container';
        container.innerHTML = '';
        container.appendChild(docContainer);
        addDownloadBar(container, docxUrl, 'document.docx');
      })
      .catch(err => {
        container.innerHTML = `
          <div style="background:#fee;border:1px solid #fcc;border-radius:8px;padding:24px;text-align:center;max-width:400px;">
            <p style="color:#c33;font-weight:700;margin:0 0 12px;"> Failed to Load Document</p>
            <p style="color:#666;margin:0;font-size:14px;">${err.message}</p>
          </div>
        `;
      });
  };
  document.head.appendChild(script);
}

//  Load spreadsheet inline 
function loadSheetInline(sheetUrl, container) {
  fetch(sheetUrl)
    .then(response => response.text())
    .then(text => {
      const rows = text.split('\n').map(row => row.split(','));
      const table = document.createElement('table');
      table.style.cssText = 'width:100%;border-collapse:collapse;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.1);';
      
      rows.forEach((row, rowIdx) => {
        const tr = document.createElement('tr');
        tr.style.cssText = rowIdx % 2 === 0 ? 'background:#fff;' : 'background:#f9f9f9;';
        row.forEach(cell => {
          const td = document.createElement('td');
          td.textContent = cell;
          td.style.cssText = 'border:1px solid #ddd;padding:12px;text-align:left;font-size:14px;color:#333;';
          tr.appendChild(td);
        });
        table.appendChild(tr);
      });
      
      container.innerHTML = '';
      container.style.cssText = 'overflow-x:auto;';
      container.appendChild(table);
    })
    .catch(err => {
      container.innerHTML = `
        <div style="background:#fee;border:1px solid #fcc;border-radius:8px;padding:24px;text-align:center;max-width:400px;">
          <p style="color:#c33;font-weight:700;margin:0 0 12px;"> Failed to Load Spreadsheet</p>
          <p style="color:#666;margin:0;font-size:14px;">${err.message}</p>
        </div>
      `;
    });
}

//  Load generic document (text, etc) 
function loadGenericDocument(docUrl, container, ext) {
  fetch(docUrl)
    .then(response => response.text())
    .then(text => {
      const docContainer = document.createElement('div');
      docContainer.style.cssText = 'background:#fff;border-radius:8px;padding:32px;max-width:900px;margin:0 auto;box-shadow:0 2px 8px rgba(0,0,0,0.1);overflow-y:auto;max-height:100%;';
      
      if (ext === 'txt') {
        const pre = document.createElement('pre');
        pre.textContent = text;
        pre.style.cssText = 'font-family:monospace;font-size:14px;color:#333;white-space:pre-wrap;word-wrap:break-word;line-height:1.6;margin:0;';
        docContainer.appendChild(pre);
      } else {
        docContainer.textContent = text;
        docContainer.style.cssText += 'white-space:pre-wrap;word-wrap:break-word;line-height:1.6;font-size:14px;color:#333;';
      }
      
      container.innerHTML = '';
      container.appendChild(docContainer);
    })
    .catch(err => {
      container.innerHTML = `
        <div style="background:#fee;border:1px solid #fcc;border-radius:8px;padding:24px;text-align:center;max-width:400px;">
          <p style="color:#c33;font-weight:700;margin:0 0 12px;"> Failed to Load Document</p>
          <p style="color:#666;margin:0;font-size:14px;">${err.message}</p>
        </div>
      `;
    });
}

export { openDocEnhanced };


